import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES, ABOUT_CHAPTERS, PASSES, AMENITIES, LOCATION } from "../data/content";
import { Reveal } from "../components/Reveal";import { VHSImage } from "../components/VHSImage";
import { BookButton } from "../components/Button";
import { EmailCapture } from "../components/EmailCapture";

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  return (
    <section ref={ref} className="relative h-[100svh] overflow-hidden bg-espresso" data-testid="hero">
      <motion.div style={{ y, scale }} className="grain absolute inset-0">
        <img src="/hero-village-v2.jpg" alt="Bathers at the baths, mid-century" className="h-full w-full object-cover grayscale contrast-[1.08]" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 z-20 bg-ink" />

      {/* Top badge */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
        className="absolute left-5 right-5 top-28 z-30 flex items-center justify-between text-cream md:left-10 md:right-10"
      >
        <span className="text-xs uppercase tracking-[0.25em]">Your Local</span>
        <span className="text-xs uppercase tracking-[0.25em]">Bathhouse</span>
      </motion.div>

      {/* Centered wordmark + address — one linked lockup that scales together */}
      <div className="absolute inset-0 z-30 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
          data-testid="hero-lockup"
          className="flex w-full max-w-[300px] flex-col items-center text-center sm:max-w-md md:max-w-2xl lg:max-w-3xl"
        >
          <img
            src="/gvb-wordmark-cream.png"
            alt="Greenwich Village Baths"
            data-testid="hero-wordmark"
            className="w-full"
          />
          <p
            data-testid="hero-address"
            className="mt-5 text-sm uppercase leading-relaxed tracking-[0.28em] text-cream/90 sm:text-base md:mt-8 md:text-xl lg:text-2xl"
          >
            24 East 13th Street
            <br />
            New York
          </p>
        </motion.div>
      </div>

      {/* Bottom content */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
        className="absolute inset-x-0 bottom-0 z-30 flex items-stretch justify-center gap-3 px-4 pb-10 md:px-10 md:pb-14"
      >
        <BookButton className="flex-1 justify-center whitespace-nowrap !px-3 !text-xs md:flex-none md:!px-8 md:!text-sm" />
        <a
          href="#spaces"
          data-testid="hero-explore-link"
          className="group flex flex-1 items-center justify-center gap-2 whitespace-nowrap border border-cream px-3 py-4 text-xs font-medium uppercase tracking-[0.12em] text-cream transition-colors duration-300 hover:bg-cream hover:text-ink md:flex-none md:px-8 md:text-sm"
        >
          Explore the space
          <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
        </a>
      </motion.div>
    </section>
  );
};

const AboutTeaser = () => (
  <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36" data-testid="about-teaser">
    <div className="grid gap-14 md:grid-cols-12">
      <div className="md:col-span-5">
        <div className="mb-8 flex items-center gap-4">
          <span className="text-xs uppercase tracking-[0.25em] text-terracotta">01 — The Heritage</span>
        </div>
        <Reveal>
          <p className="text-3xl font-medium leading-[1.15] tracking-tight md:text-[2.6vw]">
            {ABOUT_CHAPTERS[0].body}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-md text-base font-normal leading-relaxed text-ink/70">
            {ABOUT_CHAPTERS[2].body}
          </p>
          <Link
            to="/about"
            data-testid="about-teaser-link"
            className="group mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.12em] text-ink"
          >
            <span className="border-b border-ink pb-1 transition-colors group-hover:border-terracotta group-hover:text-terracotta">
              Read our story
            </span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <Reveal delay={0.05}>
          <VHSImage
            src={IMAGES.nyhrcBuilding}
            alt="The New York Health and Racquet Club building at 24 East 13th Street"
            className="aspect-[4/3] w-full"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

const PassesTeaser = () => (
  <section className="border-y border-ink bg-cream" data-testid="passes-teaser">
    <div className="mx-auto max-w-[1600px] px-5 md:px-10">
      <div className="flex flex-col gap-2 py-10 sm:flex-row sm:items-end sm:justify-between">
        <h2 className="text-4xl font-medium uppercase leading-none tracking-tight md:text-6xl">Passes</h2>
        <span className="text-sm font-normal text-ink/60">Prices vary by day and time</span>
      </div>
      <div className="grid gap-px border border-ink bg-ink md:grid-cols-2">
        {PASSES.map((p) => (
          <div key={p.name} className="bg-cream p-8 md:p-12">
            <div className="flex items-baseline justify-between">
              <h3 className="text-2xl font-medium tracking-tight md:text-3xl">{p.name}</h3>
              <span className="text-sm uppercase tracking-[0.15em] text-ink/60">{p.duration}</span>
            </div>
            <p className="mt-6 text-base font-normal leading-relaxed text-ink/70">{p.line}</p>
            <p className="mt-10 text-2xl font-medium tracking-tight md:text-3xl">{p.price}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 py-10">
        <BookButton />
      </div>
    </div>
  </section>
);

const AmenitiesTeaser = () => {
  const preview = AMENITIES.slice(0, 6);
  return (
    <section id="spaces" className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36" data-testid="amenities-teaser">
      <div className="mb-12 flex items-end justify-between">
        <h2 className="text-4xl font-medium uppercase leading-none tracking-tight md:text-6xl">
          The Amenities
        </h2>
        <Link
          to="/amenities"
          data-testid="amenities-teaser-link"
          className="group hidden items-center gap-2 text-sm uppercase tracking-[0.12em] md:inline-flex"
        >
          <span className="border-b border-ink pb-1 transition-colors group-hover:border-terracotta group-hover:text-terracotta">
            All amenities
          </span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-px border border-ink bg-ink lg:grid-cols-3">
        {preview.map((a, i) => (
          <Reveal key={a.name} delay={(i % 3) * 0.06} className="bg-cream">
            <div className="group flex h-full flex-col">
              <VHSImage src={a.image} alt={a.name} className="aspect-square w-full" border={false} parallax={false} />
              <div className="flex items-baseline justify-between p-4 md:p-6">
                <span className="text-sm font-medium uppercase tracking-tight md:text-lg">{a.name}</span>
                {a.temp && <span className="text-xs uppercase tracking-[0.15em] text-terracotta">{a.temp}</span>}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const LocationBlock = () => (
  <section className="bg-terracotta text-espresso" data-testid="location-block">
    <div className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32">
      <div className="grid gap-14 md:grid-cols-12">
        <div className="md:col-span-7">
          <span className="text-xs uppercase tracking-[0.25em]">Location & Hours</span>
          <p className="mt-8 text-5xl font-medium leading-[0.95] tracking-tight md:text-[5vw]">
            {LOCATION.address1}
          </p>
          <p className="mt-2 text-2xl font-normal tracking-tight md:text-3xl">{LOCATION.address2}</p>
          <a
            href={LOCATION.mapUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="map-link"
            className="group mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.12em]"
          >
            <span className="border-b border-espresso pb-1">Open in maps</span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
        <div className="space-y-8 md:col-span-5">
          <div>
            <p className="mb-3 text-xs uppercase tracking-[0.2em] opacity-70">Hours</p>
            {LOCATION.hours.map((h) => (
              <div key={h.days} className="flex justify-between border-b border-espresso/30 py-3 text-lg">
                <span>{h.days}</span>
                <span className="font-normal">{h.time}</span>
              </div>
            ))}
          </div>
          <div>
            <p className="mb-3 text-xs uppercase tracking-[0.2em] opacity-70">Nearest Subway</p>
            <p className="text-lg">{LOCATION.subway}</p>
          </div>
          <div className="flex flex-wrap gap-x-10 gap-y-2 text-lg">
            <a href={`tel:${LOCATION.phone}`} className="border-b border-espresso pb-0.5">{LOCATION.phone}</a>
            <a href={`mailto:${LOCATION.email}`} className="border-b border-espresso pb-0.5">{LOCATION.email}</a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const JoinBlock = () => (
  <section id="join" className="relative overflow-hidden bg-ink text-cream" data-testid="join-block">
    <div className="mx-auto max-w-[1600px] px-5 pt-24 pb-16 md:px-10 md:pt-40 md:pb-24">
      <div className="flex flex-col md:flex-row md:items-center md:gap-16">
        <Reveal className="md:order-2 md:flex md:w-1/2 md:justify-center">
          <img
            src="/sauna-scene.webp"
            alt="Illustration of bathers in the sauna"
            data-testid="sauna-scene-illustration"
            className="mb-12 w-full max-w-xs opacity-90 md:mb-0 md:max-w-md"
          />
        </Reveal>
        <div className="md:order-1 md:w-1/2">
          <span className="text-xs uppercase tracking-[0.25em] text-terracotta">Newsletter</span>
          <h2 className="mt-8 max-w-4xl text-5xl font-medium uppercase leading-[0.9] tracking-tighter md:text-[4.5vw]">
            Join the list
          </h2>
          <p className="mt-8 max-w-lg text-lg font-normal leading-relaxed text-cream/75">
            Sign up for news on upcoming events.
          </p>
          <div className="mt-12">
            <EmailCapture invert />
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.15em] text-cream/40">Bring a bathing suit.</p>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <AboutTeaser />
      <PassesTeaser />
      <AmenitiesTeaser />
      <LocationBlock />
      <JoinBlock />
    </div>
  );
}
